// ListCraft.js - Modified with multiple search fields
import React, { useState, useEffect, useMemo } from 'react';
import { useNode, useEditor } from "@craftjs/core";
import {
    FormControl,
    FormLabel,
    TextField,
    Switch,
    FormControlLabel,
    Checkbox,
    Button,
    IconButton,
    Box
} from "@mui/material";
import { Delete as DeleteIcon, Add as AddIcon, ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { ListProvider } from '../../../List/ListContext';
import { VirtualCraftList } from './VirtualCraftList';
import { CardCraft } from './CardCraft';
import { QwantaProvider } from 'qwanyx';

export const ListCraft = ({
    name = "ListCraft",
    queryString = "{}",
    sortString = "{}",
    projectionString = "{}",
    batchSize = 20,
    threshold = 300,
    loadAllAtOnce = true,
    clickable = true,
    searchable = false,
    searchFields = [{ key: 'title', label: 'Title' }], // CHANGED: now array
    searchPlaceholder = 'Search...',
    width = "100%",
    height = "400px",
    hidden = false,
    style = {},
    displayFields = [
        { key: '_id', label: 'ID' }
    ],
    showAllFields = false,
    detailId = "card",
    ...props
}) => {
    const {
        connectors: { connect, drag },
        isSelected,
        isHovered,
    } = useNode((state) => ({
        isSelected: state.events.selected,
        isHovered: state.events.hovered,
    }));

    const { enabled } = useEditor((state) => ({
        enabled: state.options.enabled
    }));

    const query = useMemo(() => {
        try {
            return JSON.parse(queryString || '{}');
        } catch {
            return {};
        }
    }, [queryString]);

    const sort = useMemo(() => {
        try {
            return JSON.parse(sortString || '{}');
        } catch {
            return {};
        }
    }, [sortString]);

    const projection = useMemo(() => {
        try {
            return JSON.parse(projectionString || '{}');
        } catch {
            return {};
        }
    }, [projectionString]);

    const [selectedItem, setSelectedItem] = useState(null);
    const [searchInput, setSearchInput] = useState('');
    const [activeSearchTerm, setActiveSearchTerm] = useState('');
    const [combinedQuery, setCombinedQuery] = useState(query);
    const [listKey, setListKey] = useState(0);

    const getSelectionStyle = () => {
        if (!enabled) return {};

        if (isSelected) {
            return {
                outline: '1px dashed #2196f3',
                outlineOffset: '2px'
            };
        }
        if (isHovered) {
            return {
                outline: '1px dashed #ff9800',
                outlineOffset: '1px'
            };
        }
        return {};
    };

    // UPDATED: Search logic for multiple fields
    useEffect(() => {
        if (!activeSearchTerm.trim()) {
            if (Array.isArray(query) && query.length > 0 && query[0].$search) {
                const emptySearchQuery = [...query];
                if (emptySearchQuery[0].$search.compound && emptySearchQuery[0].$search.compound.must) {
                    emptySearchQuery[0].$search.compound.must = emptySearchQuery[0].$search.compound.must.filter(
                        condition => condition.exists
                    );
                    emptySearchQuery[0].$search.compound.must.unshift({
                        text: {
                            query: "EMPTY_SEARCH_PLACEHOLDER_XYZ",
                            path: { wildcard: "*" }
                        }
                    });
                }
                setCombinedQuery(emptySearchQuery);
            } else {
                setCombinedQuery(query);
            }
            setListKey(prevKey => prevKey + 1);
            return;
        }

        const searchTerms = activeSearchTerm.trim().split(/\s+/);

        if (Array.isArray(query) && query.length > 0 && query[0].$search) {
            // Atlas Search - unchanged
            const searchQuery = [...query];
            let existsCondition = null;

            if (searchQuery[0].$search.compound && searchQuery[0].$search.compound.must) {
                searchQuery[0].$search.compound.must.forEach(condition => {
                    if (condition.exists) {
                        existsCondition = condition;
                    }
                });
            }

            const mustConditions = [];
            searchTerms.forEach(term => {
                mustConditions.push({
                    text: {
                        query: term,
                        path: { wildcard: "*" }
                    }
                });
            });

            if (existsCondition) {
                mustConditions.push(existsCondition);
            }

            searchQuery[0].$search.compound.must = mustConditions;
            setCombinedQuery(searchQuery);
        } else {
            // UPDATED: Regular search with multiple fields
            const searchTerms = activeSearchTerm.trim().split(/\s+/);
            
            const andConditions = searchTerms.map(term => {
                // Create $or condition for each term across all search fields
                const orConditions = searchFields.map(field => ({
                    [field.key]: { $regex: term, $options: 'i' }
                }));
                
                return { $or: orConditions };
            });

            setCombinedQuery({
                ...query,
                $and: andConditions
            });
        }

        setListKey(prevKey => prevKey + 1);
    }, [query, activeSearchTerm, searchFields]); // UPDATED: dependency on searchFields

    if (hidden) return null;

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setActiveSearchTerm(searchInput);
    };

    const handleItemClick = (item) => {
        if (!item || !clickable) return;
        setSelectedItem(item);
    };

    const handleBackToList = () => {
        setSelectedItem(null);
    };

    const DetailWrapper = ({ item }) => {
        return (
            <div>
                <IconButton onClick={handleBackToList}>
                    <ArrowBackIcon />
                </IconButton>

                {detailId ? (
                    <QwantaProvider _id={item._id || "guest"}>
                        <CardCraft documentId={detailId} editable={false} />
                    </QwantaProvider>
                ) : (
                    <div style={{ padding: '12px', border: '1px solid #eee' }}>
                        {showAllFields ? (
                            Object.entries(item).map(([key, value]) => (
                                <div key={key} style={{ marginBottom: '4px' }}>
                                    <strong>{key}:</strong> {String(value)}
                                </div>
                            ))
                        ) : (
                            displayFields.map(field => (
                                <div key={field.key} style={{ marginBottom: '4px' }}>
                                    {field.label ? (
                                        <>
                                            <strong>{field.label}:</strong> {String(item[field.key] || '')}
                                        </>
                                    ) : (
                                        String(item[field.key] || '')
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                )}
            </div>
        );
    };

    const containerStyle = {
        width,
        height,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        transition: 'all 0.2s ease',
        position: 'relative',
        ...style,
        ...getSelectionStyle()
    };

    return (
        <div
            ref={ref => enabled ? connect(drag(ref)) : connect(ref)}
            name={name}
            style={containerStyle}
            {...props}
        >
            <ListProvider
                key={listKey}
                query={combinedQuery}
                sort={sort}
                projection={projection}
                batchSize={batchSize}
                autoLoadThreshold={threshold}
                loadAllAtOnce={loadAllAtOnce}
                initialItems={[]}
            >
                {selectedItem && <DetailWrapper item={selectedItem} />}

                <div style={{
                    display: selectedItem ? 'none' : 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    height: '100%',
                    overflow: 'hidden'
                }}>
                    {searchable && (
                        <div style={{
                            display: 'flex',
                            marginBottom: '15px',
                            flexShrink: 0
                        }}>
                            <form onSubmit={handleSearchSubmit} style={{ display: 'flex', width: '100%' }}>
                                <input
                                    type="text"
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                    placeholder={searchPlaceholder}
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        fontSize: '16px',
                                        border: '1px solid #ddd',
                                        borderTopRightRadius: 0,
                                        borderBottomRightRadius: 0,
                                    }}
                                />
                                <button
                                    type="submit"
                                    style={{
                                        padding: '10px 15px',
                                        backgroundColor: '#2196F3',
                                        color: 'white',
                                        border: 'none',
                                        borderTopRightRadius: '4px',
                                        borderBottomRightRadius: '4px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Search
                                </button>
                            </form>
                        </div>
                    )}

                    <div style={{ flex: 1, overflow: 'hidden' }}>
                        <VirtualCraftList
                            onItemClick={handleItemClick}
                            clickable={clickable}
                            displayFields={displayFields}
                            showAllFields={showAllFields}
                        />
                    </div>
                </div>
            </ListProvider>
        </div>
    );
};

// UPDATED: Settings with multiple search fields
const ListCraftSettings = () => {
    const {
        actions: { setProp },
        name,
        queryString,
        sortString,
        projectionString,
        batchSize,
        threshold,
        loadAllAtOnce,
        clickable,
        searchable,
        searchFields, // CHANGED
        searchPlaceholder,
        width,
        height,
        displayFields,
        showAllFields,
        detailId
    } = useNode((node) => ({
        name: node.data.props.name,
        queryString: node.data.props.queryString,
        sortString: node.data.props.sortString,
        projectionString: node.data.props.projectionString,
        batchSize: node.data.props.batchSize,
        threshold: node.data.props.threshold,
        loadAllAtOnce: node.data.props.loadAllAtOnce,
        clickable: node.data.props.clickable,
        searchable: node.data.props.searchable,
        searchFields: node.data.props.searchFields, // CHANGED
        searchPlaceholder: node.data.props.searchPlaceholder,
        width: node.data.props.width,
        height: node.data.props.height,
        displayFields: node.data.props.displayFields,
        showAllFields: node.data.props.showAllFields,
        detailId: node.data.props.detailId
    }));

    const [localName, setLocalName] = useState(name);
    const [localQuery, setLocalQuery] = useState(queryString || '');
    const [localSort, setLocalSort] = useState(sortString || '');
    const [localProjection, setLocalProjection] = useState(projectionString || '');
    const [localWidth, setLocalWidth] = useState(width);
    const [localHeight, setLocalHeight] = useState(height);
    const [localSearchPlaceholder, setLocalSearchPlaceholder] = useState(searchPlaceholder);
    const [localDetailId, setLocalDetailId] = useState(detailId);

    useEffect(() => setLocalName(name), [name]);
    useEffect(() => setLocalQuery(queryString || ''), [queryString]);
    useEffect(() => setLocalSort(sortString || ''), [sortString]);
    useEffect(() => setLocalProjection(projectionString || ''), [projectionString]);
    useEffect(() => setLocalWidth(width), [width]);
    useEffect(() => setLocalHeight(height), [height]);
    useEffect(() => setLocalSearchPlaceholder(searchPlaceholder), [searchPlaceholder]);
    useEffect(() => setLocalDetailId(detailId || ''), [detailId]);

    const handleJSONBlur = (propName, value) => {
        const trimmedValue = value.trim();
        if (trimmedValue === '') {
            setProp(props => props[propName] = '{}');
        } else {
            try {
                JSON.parse(trimmedValue);
                setProp(props => props[propName] = trimmedValue);
            } catch (err) {
                console.log(`Invalid JSON in ${propName}:`, err);
                if (propName === 'queryString') {
                    setLocalQuery(queryString || '');
                } else if (propName === 'sortString') {
                    setLocalSort(sortString || '');
                } else if (propName === 'projectionString') {
                    setLocalProjection(projectionString || '');
                }
            }
        }
    };

    const addField = () => {
        setProp(props => {
            props.displayFields = [...props.displayFields, {
                key: `field${props.displayFields.length + 1}`,
                label: `Field ${props.displayFields.length + 1}`
            }];
        });
    };

    const removeField = (index) => {
        setProp(props => {
            props.displayFields = props.displayFields.filter((_, i) => i !== index);
        });
    };

    const updateField = (index, field, value) => {
        setProp(props => {
            props.displayFields[index][field] = value;
        });
    };

    // NEW: Search field management functions
    const addSearchField = () => {
        setProp(props => {
            props.searchFields = [...props.searchFields, {
                key: `field${props.searchFields.length + 1}`,
                label: `Field ${props.searchFields.length + 1}`
            }];
        });
    };

    const removeSearchField = (index) => {
        setProp(props => {
            props.searchFields = props.searchFields.filter((_, i) => i !== index);
        });
    };

    const updateSearchField = (index, field, value) => {
        setProp(props => {
            props.searchFields[index][field] = value;
        });
    };

    return (
        <div>
            <FormControl fullWidth margin="normal">
                <FormLabel>Component Name</FormLabel>
                <TextField
                    value={localName || ''}
                    onChange={(e) => setLocalName(e.target.value)}
                    onBlur={(e) => setProp(props => props.name = e.target.value)}
                    size="small"
                />
            </FormControl>

            <FormControl fullWidth margin="normal">
                <FormLabel>Detail Card ID (qwantumId for detail view)</FormLabel>
                <TextField
                    value={localDetailId || ''}
                    onChange={(e) => setLocalDetailId(e.target.value)}
                    onBlur={(e) => setProp(props => props.detailId = e.target.value)}
                    size="small"
                    placeholder="detail, itemDetail, etc."
                />
            </FormControl>

            <FormControl fullWidth margin="normal">
                <FormLabel>Query JSON</FormLabel>
                <TextField
                    multiline
                    rows={4}
                    value={localQuery}
                    onChange={(e) => setLocalQuery(e.target.value)}
                    onBlur={(e) => handleJSONBlur('queryString', e.target.value)}
                    size="small"
                    placeholder='{"di": "your_domain", "status": "active"}'
                />
            </FormControl>

            <FormControl fullWidth margin="normal">
                <FormLabel>Sort JSON</FormLabel>
                <TextField
                    multiline
                    rows={2}
                    value={localSort}
                    onChange={(e) => setLocalSort(e.target.value)}
                    onBlur={(e) => handleJSONBlur('sortString', e.target.value)}
                    size="small"
                    placeholder='{"createdAt": -1}'
                />
            </FormControl>

            <FormControl fullWidth margin="normal">
                <FormLabel>Projection JSON</FormLabel>
                <TextField
                    multiline
                    rows={2}
                    value={localProjection}
                    onChange={(e) => setLocalProjection(e.target.value)}
                    onBlur={(e) => handleJSONBlur('projectionString', e.target.value)}
                    size="small"
                    placeholder='{"title": 1, "description": 1}'
                />
            </FormControl>

            <FormControl fullWidth margin="normal">
                <FormLabel>Width</FormLabel>
                <TextField
                    value={localWidth}
                    onChange={(e) => setLocalWidth(e.target.value)}
                    onBlur={(e) => setProp(props => props.width = e.target.value)}
                    size="small"
                    placeholder="100%, 500px, auto"
                />
            </FormControl>

            <FormControl fullWidth margin="normal">
                <FormLabel>Height</FormLabel>
                <TextField
                    value={localHeight}
                    onChange={(e) => setLocalHeight(e.target.value)}
                    onBlur={(e) => setProp(props => props.height = e.target.value)}
                    size="small"
                    placeholder="400px, 100vh, auto"
                />
            </FormControl>

            <FormControl fullWidth margin="normal">
                <FormLabel>Batch Size</FormLabel>
                <TextField
                    type="number"
                    value={batchSize}
                    onChange={(e) => setProp(props => props.batchSize = parseInt(e.target.value) || 20)}
                    size="small"
                    inputProps={{ min: 1, max: 1000 }}
                />
            </FormControl>

            <FormControl fullWidth margin="normal">
                <FormLabel>Search Placeholder</FormLabel>
                <TextField
                    value={localSearchPlaceholder}
                    onChange={(e) => setLocalSearchPlaceholder(e.target.value)}
                    onBlur={(e) => setProp(props => props.searchPlaceholder = e.target.value)}
                    size="small"
                    placeholder="Search items..."
                />
            </FormControl>

            <FormControlLabel
                control={
                    <Switch
                        checked={loadAllAtOnce}
                        onChange={(e) => setProp(props => props.loadAllAtOnce = e.target.checked)}
                    />
                }
                label="Load All At Once"
            />

            <FormControlLabel
                control={
                    <Switch
                        checked={clickable}
                        onChange={(e) => setProp(props => props.clickable = e.target.checked)}
                    />
                }
                label="Clickable Items"
            />

            <FormControlLabel
                control={
                    <Switch
                        checked={searchable}
                        onChange={(e) => setProp(props => props.searchable = e.target.checked)}
                    />
                }
                label="Enable Search"
            />

            {/* NEW: Search Fields Management */}
            {searchable && (
                <FormControl fullWidth margin="normal">
                    <FormLabel>Search Fields (Fields to search in)</FormLabel>
                    <Box>
                        {searchFields?.map((field, index) => (
                            <Box key={index} display="flex" alignItems="center" gap={1} marginBottom={1}>
                                <TextField
                                    size="small"
                                    placeholder="Field Key"
                                    value={field.key}
                                    onChange={(e) => updateSearchField(index, 'key', e.target.value)}
                                    style={{ flex: 1 }}
                                />
                                <TextField
                                    size="small"
                                    placeholder="Display Label"
                                    value={field.label}
                                    onChange={(e) => updateSearchField(index, 'label', e.target.value)}
                                    style={{ flex: 2 }}
                                />
                                <IconButton
                                    size="small"
                                    onClick={() => removeSearchField(index)}
                                    disabled={searchFields.length <= 1}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Box>
                        ))}
                        <Button
                            startIcon={<AddIcon />}
                            onClick={addSearchField}
                            size="small"
                            variant="outlined"
                        >
                            Add Search Field
                        </Button>
                    </Box>
                </FormControl>
            )}

            <FormControlLabel
                control={
                    <Checkbox
                        checked={showAllFields}
                        onChange={(e) => setProp(props => props.showAllFields = e.target.checked)}
                    />
                }
                label="Show All Fields (ignore selected fields below)"
            />

            <FormControl fullWidth margin="normal">
                <FormLabel>Display Fields (Select which fields to show when no template)</FormLabel>
                <Box>
                    {displayFields?.map((field, index) => (
                        <Box key={index} display="flex" alignItems="center" gap={1} marginBottom={1}>
                            <TextField
                                size="small"
                                placeholder="Field Key"
                                value={field.key}
                                onChange={(e) => updateField(index, 'key', e.target.value)}
                                style={{ flex: 1 }}
                            />
                            <TextField
                                size="small"
                                placeholder="Display Label"
                                value={field.label}
                                onChange={(e) => updateField(index, 'label', e.target.value)}
                                style={{ flex: 2 }}
                            />
                            <IconButton
                                size="small"
                                onClick={() => removeField(index)}
                                disabled={displayFields.length <= 1}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Box>
                    ))}
                    <Button
                        startIcon={<AddIcon />}
                        onClick={addField}
                        size="small"
                        variant="outlined"
                    >
                        Add Field
                    </Button>
                </Box>
            </FormControl>
        </div>
    );
};

// UPDATED: Craft config with searchFields array
ListCraft.craft = {
    props: {
        name: "ListCraft",
        queryString: "{}",
        sortString: "{}",
        projectionString: "{}",
        batchSize: 20,
        threshold: 300,
        loadAllAtOnce: true,
        clickable: true,
        searchable: false,
        searchFields: [{ key: 'title', label: 'Title' }], // CHANGED
        searchPlaceholder: 'Search...',
        width: "100%",
        height: "400px",
        displayFields: [
            { key: '_id', label: 'ID' }
        ],
        showAllFields: false,
        detailId: "detail"
    },
    related: {
        settings: ListCraftSettings
    }
};